import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Campaign } from '../types';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';
import CampaignStatusChip from '../components/CampaignStatusChip';

const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isReady } = useTelegramWebApp();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        // Логируем для отладки
        console.log('🔍 Fetching campaigns for user:', { 
          user, 
          userId: user?.id, 
          isReady 
        });

        // Передаем telegram_user_id если пользователь авторизован
        const campaignsData = await apiService.getCampaigns(user?.id);
        setCampaigns(campaignsData);
      } catch (err: any) {
        console.error('❌ Failed to fetch campaigns:', err);
        setError(err.message || 'Не удалось загрузить кампании');
      } finally {
        setLoading(false);
      }
    };

    // Ждем готовности WebApp перед запросом
    if (isReady) {
      fetchCampaigns();
    }
  }, [user?.id, isReady]);

  const handleGoBack = () => {
    navigate('/');
  };

  const handleViewDetails = (campaignId: number) => {
    navigate(`/campaigns/${campaignId}`);
  };


  const getGoalText = (goal: string | null) => {
    if (!goal) return 'Не указано';
    switch (goal) {
      case 'sales': return 'Продажи';
      case 'leads': return 'Лиды';
      case 'awareness': return 'Узнаваемость';
      default: return goal;
    }
  };

  // Показываем загрузку пока WebApp не готов
  if (!isReady) {
    return (
      <Container maxWidth="sm" sx={{ py: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#4d648d' }} />
      </Container>
    );
  }

  // Показываем сообщение если пользователь не авторизован
  if (!user?.id) {
    return (
      <Container maxWidth="sm" sx={{ py: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Необходимо авторизоваться через Telegram для просмотра кампаний
        </Alert>
        <Button onClick={handleGoBack} variant="contained" sx={{ color: '#ffffff !important' }}>
          Назад
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      {/* Header with back button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        position: 'relative',
        mb: 3,
      }}>
        <Button
          onClick={handleGoBack}
          sx={{ 
            position: 'absolute',
            left: 0,
            minWidth: 48, 
            width: 48, 
            height: 48, 
            color: '#000000',
            p: 0,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            },
            '&:focus': {
              backgroundColor: 'transparent'
            },
            '&:focus-visible': {
              backgroundColor: 'transparent',
              outline: 'none'
            }
          }}
        >
          <ArrowBack />
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
          Кампании
        </Typography>
      </Box>

      {/* Full-width Create Campaign button */}
      <Button 
        variant="contained" 
        fullWidth
        onClick={() => navigate('/campaigns/create')}
        sx={{
          mb: 3,
          py: 1.5,
          borderRadius: 2,
          background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
          color: '#ffffff !important',
          boxShadow: 'none',
          '&:hover': {
            background: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
            opacity: 0.9,
            boxShadow: 'none',
            color: '#ffffff !important'
          }
        }}
      >
        Создать кампанию
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#4d648d' }} />
          <Typography variant="body2" sx={{ color: '#000000', mt: 2 }}>
            Загружаем кампании...
          </Typography>
        </Box>
      ) : campaigns.length === 0 ? (
        <Card elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              У вас пока нет кампаний
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Создайте свою первую рекламную кампанию, чтобы начать привлекать клиентов
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/onboarding')}
              sx={{
                backgroundColor: '#283655',
                '&:hover': {
                  backgroundColor: '#283655',
                  opacity: 0.8
                }
              }}
            >
              Создать первую кампанию
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {campaigns.map((campaign) => (
            <Grid key={campaign.id} item xs={12}>
              <Card elevation={0} sx={{ bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {campaign.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Цель: {getGoalText(campaign.goal)} • Бюджет: {campaign.budget ? campaign.budget.toLocaleString('ru-RU') : '0'} ₽
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Сайт: {campaign.campaign_data?.site_url}
                      </Typography>
                    </Box>
                    <CampaignStatusChip 
                      status={campaign.status}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={() => handleViewDetails(campaign.id)}
                      sx={{
                        borderColor: '#283655',
                        color: '#000000',
                        '&:hover': {
                          borderColor: '#283655',
                          backgroundColor: 'rgba(40, 54, 85, 0.04)'
                        }
                      }}
                    >
                      Подробнее
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default CampaignsPage;


